import React, {forwardRef, useMemo, useRef, useState} from 'react';

import Header from '@Themes/natucare/modules/Header/Header';
import HeaderV4 from '@Themes/natucare/modules/V4/Header/Header';
import localHeaderData from '@/data/pl/header/localHeaderData';

import {transformProductsData} from './utils/transformProductsData';

import {getSearchResults} from './api/getSearchResults';
import {saveSearch} from './api/saveSearch';

const initialState = {
  status: {
    isFetching: false,
    isLoaded: true
  }
};

const MAIN_BAR_OVERRIDE = localHeaderData?.mainBar || null; // Set to null to use data from the CMS

const isVisible = item => item && item.visible !== false;

const normalizeNav = nav => {
  if (!nav) {
    return undefined;
  }

  const rawItems = nav.items;
  let items = [];

  if (Array.isArray(rawItems)) {
    items = rawItems.filter(isVisible);
  } else if (Array.isArray(rawItems?.items)) {
    items = rawItems.items.filter(isVisible);
  } else if (rawItems && typeof rawItems === 'object') {
    items = Object.values(rawItems).filter(isVisible);
  }

  return {
    ...nav,
    items
  };
};

const normalizeHeaderItemsCollection = headerItemsCollection => {
  if (!headerItemsCollection) {
    return undefined;
  }

  if (Array.isArray(headerItemsCollection)) {
    return headerItemsCollection.filter(isVisible);
  }

  if (Array.isArray(headerItemsCollection?.items)) {
    return headerItemsCollection.items.filter(isVisible);
  }

  return undefined;
};

const HeaderApi = forwardRef(({headerData, isBlogPostTemplate, useLegacyHeader = false}, readingIndicatorRef) => {
  const [searchResults, setSearchResults] = useState(initialState);

  const [searchPhrase, setSearchPhrase] = useState('');

  const lastSearchTermRef = useRef(null);

  const normalizedHeaderData = useMemo(() => {
    if (useLegacyHeader) {
      return headerData || {};
    }

    const sourceMainBar = MAIN_BAR_OVERRIDE || headerData?.mainBar;

    const mainBar = sourceMainBar ? {
      ...sourceMainBar,
      nav: normalizeNav(sourceMainBar.nav),
      headerItemsCollection: normalizeHeaderItemsCollection(sourceMainBar.headerItemsCollection)
    } : undefined;

    return {
      promoBar: headerData?.promoBar,
      mainBar,
      searchResults: headerData?.searchResults
    };
  }, [headerData, useLegacyHeader]);

  const handleSearch = async e => {
    const searchTerm = e.trim();

    setSearchPhrase(searchTerm);

    if (!searchTerm) {
      lastSearchTermRef.current = '';
      setSearchResults(initialState);
      return;
    }

    if (searchTerm === lastSearchTermRef.current) {
      return;
    }

    lastSearchTermRef.current = searchTerm;

    setSearchResults({
      status: {
        isFetching: true,
        isLoaded: false
      }
    });

    try {
      const {shopifyProducts} = await getSearchResults(searchTerm);

      const newProductsItems = transformProductsData(shopifyProducts);

      setSearchResults({
        status: {
          isFetching: false,
          isLoaded: true
        },
        popularSearches: [
          {
            label: 'Kolagen',
            href: 'https://shop.natu.care/collections#kolagen'
          },
          {
            label: 'Omega-3',
            href: 'https://shop.natu.care/collections#omega-3'
          },
          {
            label: 'Maślan sodu',
            href: 'https://shop.natu.care/collections#maslan-sodu'
          }
        ],
        notFoundDescription: 'Przykro nam ale nie znaleźliśmy nic pod hasłem',
        stringNotFound: (newProductsItems.length === 0) ? searchTerm : undefined,
        ...(newProductsItems.length > 0 && {
          products: {
            title: 'Sklep',
            items: newProductsItems,
            button: {
              label: 'zobacz wszystkie produkty',
              href: 'https://shop.natu.care/collections'
            }
          }
        })
      });
    } catch (error) {
      setSearchResults({
        status: {
          isFetching: false,
          isLoaded: true
        }
      });
    }
  };

  const hasActiveSearch = searchResults !== initialState;
  const resultsToDisplay = hasActiveSearch
    ? searchResults
    : {
      ...initialState,
      ...(normalizedHeaderData.searchResults || {})
    };
  if (useLegacyHeader) {
    return <Header
      {...(headerData || {})}
      ref={readingIndicatorRef}
      isBlogPostTemplate={isBlogPostTemplate}
      searchResults={resultsToDisplay}
      searchTerm={searchPhrase}
      sendSearchData={saveSearch}
      onChange={handleSearch}
    />;
  }

  return <HeaderV4
    ref={readingIndicatorRef}
    promoBar={normalizedHeaderData.promoBar}
    mainBar={normalizedHeaderData.mainBar}
    onChange={handleSearch}
    searchResults={resultsToDisplay}
    searchTerm={searchPhrase}
    sendSearchData={saveSearch}
    isBlogPostTemplate={isBlogPostTemplate}
  />;
});

export default HeaderApi;
